import { RowAction } from '../types';

type BuildRequestOptions<T> = {
	action: RowAction;
	selectedRecords: T[];
	rowKey?: keyof T;
};

export const buildRequestFromRecord = <T extends Record<string, any>>({
	action,
	selectedRecords,
	rowKey = 'main_id' as keyof T,
}: BuildRequestOptions<T>) => {
	const identifiers = selectedRecords.map((r) => r[rowKey]);
	const baseRecord = selectedRecords[0];

	const fullEndpoint = action?.pathParam
		? typeof action.endpoint === 'string'
			? `${action.endpoint}/${baseRecord?.[action.pathParam]}`
			: action.endpoint
				? action.endpoint(baseRecord?.[action.pathParam])
				: undefined
		: action?.endpoint;

	const serializeParams = (endsWithAmpersand = false) => {
		if (!action?.queryParams?.length) return '';

		const parts = action.queryParams.flatMap((param) => {
			if (param === 'identifiers') {
				return identifiers.map((id) => `identifiers=${encodeURIComponent(id)}`);
			}
			if (typeof param === 'string') {
				return [`${param}=${encodeURIComponent(baseRecord?.[param] ?? '')}`];
			}

			if (param.paramKey === 'identifiers' && 'recordKey' in param) {
				const values = selectedRecords.map((r) => r?.[param.recordKey]).filter((v) => v != null);
				return values.map((v) => `identifiers=${encodeURIComponent(String(v))}`);
			}

			if ('value' in param) {
				return [`${param.paramKey}=${encodeURIComponent(String(param.value))}`];
			}

			return [`${param.paramKey}=${encodeURIComponent(baseRecord?.[param.recordKey] ?? '')}`];
		});

		const qs = parts.join('&');
		if (!qs) return '';

		return endsWithAmpersand ? qs : `?${qs}`;
	};

	const query =
		typeof fullEndpoint === 'string' && fullEndpoint.endsWith('&')
			? serializeParams(true)
			: serializeParams(false);

	const body = action?.buildPayload
		? action.buildPayload(baseRecord)
		: action?.bodyFields?.length
			? action.bodyFields.reduce(
					(acc, field) => {
						if (typeof field === 'string') {
							acc[field] = baseRecord?.[field];
						} else if ('value' in field) {
							acc[field.bodyKey] = field.value;
						} else if ('bodyKey' in field && 'recordKey' in field) {
							acc[field.bodyKey] = selectedRecords.map(
								(record) => record[field.recordKey as keyof T],
							);
						}
						return acc;
					},
					{} as Record<string, any>,
				)
			: undefined;

	return {
		endpoint: fullEndpoint + query,
		payload: body,
		responseType: action.responseType || 'json',
		actionId: action.actionId,
	};
};
